import type { BadgeVariant } from "@/components/ui/badge";
import {
  pqrMessageSenderRoles,
  pqrCategories,
  pqrPageSize,
  pqrStatuses,
  pqrTicketRefPattern,
  type PqrAdminListItem,
  type PqrCategory,
  type PqrHistoryItem,
  type PqrMessageSenderRole,
  type PqrRecord,
  type PqrReplyRecord,
  type PqrRequestDetails,
  type PqrStatusHistoryEntry,
  type PqrStatus,
  type PqrThreadMessage,
} from "@/features/pqr/pqr.types";

const statusVariantMap: Record<PqrStatus, BadgeVariant> = {
  closed: "archived",
  in_review: "secondary",
  open: "default",
  resolved: "outline",
};

function toPqrCategory(value: string): PqrCategory {
  return isKnownPqrCategory(value) ? value : "other";
}

function toPqrStatus(value: string): PqrStatus {
  return isKnownPqrStatus(value) ? value : "open";
}

function toPqrMessageSenderRole(value: string): PqrMessageSenderRole {
  return isKnownPqrMessageSenderRole(value) ? value : "admin";
}

export function isValidPqrTicketRef(value: string) {
  return pqrTicketRefPattern.test(value);
}

export function getPqrStatusTranslationKey(status: PqrStatus) {
  return `pqr.status.${status}` as const;
}

export function getPqrCategoryTranslationKey(category: PqrCategory) {
  return `pqr.categories.${category}` as const;
}

export function getPqrStatusBadgeVariant(status: PqrStatus) {
  return statusVariantMap[status];
}

export function getPqrAttachmentFileName(path: string) {
  const parts = path.split("/");
  return parts[parts.length - 1] ?? path;
}

export function mapPqrHistoryItem(record: Pick<
  PqrRecord,
  "attachment_paths" | "category" | "created_at" | "id" | "status" | "subject" | "ticket_ref"
>): PqrHistoryItem {
  return {
    attachmentCount: Array.isArray(record.attachment_paths) ? record.attachment_paths.length : 0,
    category: toPqrCategory(record.category),
    createdAt: record.created_at,
    id: record.id,
    status: toPqrStatus(record.status),
    subject: record.subject,
    ticketRef: record.ticket_ref,
  };
}

export function mapPqrRequestDetails(record: PqrRecord): PqrRequestDetails {
  return {
    attachmentPaths: record.attachment_paths ?? [],
    category: toPqrCategory(record.category),
    createdAt: record.created_at,
    email: record.email,
    id: record.id,
    message: record.message,
    name: record.name,
    status: toPqrStatus(record.status),
    statusHistory: mapPqrStatusHistory(record.status_history),
    subject: record.subject,
    ticketRef: record.ticket_ref,
    userId: record.user_id,
  };
}

export function mapPqrThreadMessage(record: PqrReplyRecord): PqrThreadMessage {
  return {
    body: record.body,
    createdAt: record.created_at,
    id: record.id,
    senderRole: toPqrMessageSenderRole(record.sender_role),
    senderUserId: record.sender_user_id,
  };
}

export function mapPqrAdminListItem(record: Pick<
  PqrRecord,
  "category" | "created_at" | "email" | "id" | "name" | "status" | "subject" | "ticket_ref" | "user_id"
>): PqrAdminListItem {
  return {
    category: toPqrCategory(record.category),
    createdAt: record.created_at,
    email: record.email,
    id: record.id,
    name: record.name,
    status: toPqrStatus(record.status),
    subject: record.subject,
    ticketRef: record.ticket_ref,
    userId: record.user_id,
  };
}

export function mapPqrStatusHistory(value: unknown): PqrStatusHistoryEntry[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") {
        return null;
      }

      const record = entry as Record<string, unknown>;
      const status = typeof record.status === "string" ? toPqrStatus(record.status) : "open";
      const changedAt = typeof record.changed_at === "string" ? record.changed_at : "";
      if (!changedAt) {
        return null;
      }

      return {
        changedAt,
        changedBy: typeof record.changed_by === "string" ? record.changed_by : null,
        note: typeof record.note === "string" ? record.note : null,
        status,
      };
    })
    .filter((entry): entry is PqrStatusHistoryEntry => entry !== null);
}

export function createPqrPagination(input: {
  page: number;
  pageSize?: number;
  totalCount: number;
}) {
  const pageSize = input.pageSize ?? pqrPageSize;
  const pageCount = Math.max(1, Math.ceil(input.totalCount / pageSize));
  const page = Math.min(Math.max(input.page, 1), pageCount);

  return {
    page,
    pageCount,
    pageSize,
    rangeFrom: (page - 1) * pageSize,
    rangeTo: (page * pageSize) - 1,
    totalCount: input.totalCount,
  };
}

export function isKnownPqrStatus(value: string): value is PqrStatus {
  return pqrStatuses.includes(value as PqrStatus);
}

export function isKnownPqrMessageSenderRole(value: string): value is PqrMessageSenderRole {
  return pqrMessageSenderRoles.includes(value as PqrMessageSenderRole);
}

export function isKnownPqrCategory(value: string): value is PqrCategory {
  return pqrCategories.includes(value as PqrCategory);
}
